import { useEffect, useState } from "react";

import { debounce } from "lodash";

import {
  FETCH_META_SHOP_ORDER_TAG_INCLUSIONS,
  UPDATE_SHOP_ORDER_TAG_INCLUSIONS,
} from "@gql/shops-order-tag-inclusions";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type UpdateShopOrderTagInclusions,
  type UpdateShopOrderTagInclusionsVariables,
  type FetchMetaShopOrderTagInclusions,
} from "@north-beam/nb-client-side";
import { type TagBasedOrderExclusion } from "@north-beam/nb-common";

export const useMetaShopTagInclusions = () => {
  const [isSynced, setIsSynced] = useState<boolean>(true);

  const [metaShopOrderTagInclusions, setMetaShopOrderTagInclusions] = useState<
    TagBasedOrderExclusion[]
  >([]);

  const { data, loading } = useNorthbeamQuery<FetchMetaShopOrderTagInclusions>(
    FETCH_META_SHOP_ORDER_TAG_INCLUSIONS,
  );

  const [updateMetaShopOrderTagInclusions] = useNorthbeamMutation<
    UpdateShopOrderTagInclusions,
    UpdateShopOrderTagInclusionsVariables
  >(UPDATE_SHOP_ORDER_TAG_INCLUSIONS);

  useEffect(() => {
    if (data?.me?.metaShopOrderTagInclusions) {
      setMetaShopOrderTagInclusions((prevMetaShopOrderTagInclusions) => {
        if (prevMetaShopOrderTagInclusions.length === 0) {
          return data.me.metaShopOrderTagInclusions;
        }
        return prevMetaShopOrderTagInclusions;
      });
    }
  }, [data?.me?.metaShopOrderTagInclusions]);

  const debouncedUpdate = debounce(
    async (newData: TagBasedOrderExclusion[]) => {
      setIsSynced(false);
      await updateMetaShopOrderTagInclusions({
        variables: { shop: "meta", shopOrderTagInclusions: newData },
        refetchQueries: [{ query: FETCH_META_SHOP_ORDER_TAG_INCLUSIONS }],
      });
      setIsSynced(true);
    },
    250,
    { leading: false, trailing: true },
  );

  const updateOrderTagByIndex = (
    index: number,
    newData: TagBasedOrderExclusion,
  ) => {
    const copyInclusions = [...metaShopOrderTagInclusions];
    copyInclusions[index] = newData;
    setMetaShopOrderTagInclusions(copyInclusions);
    debouncedUpdate(copyInclusions);
  };

  const createOrderTagInclusion = () => {
    const newExclusion: TagBasedOrderExclusion = {
      name: "",
      tagType: "order",
      pattern: "",
      tagLogic: "is",
      active: false,
    };
    const copyInclusions = [...metaShopOrderTagInclusions, newExclusion];
    setMetaShopOrderTagInclusions(copyInclusions);
    debouncedUpdate(copyInclusions);
  };

  const removeOrderTagInclusion = (index: number) => {
    const copyInclusions = [...metaShopOrderTagInclusions];
    copyInclusions.splice(index, 1);
    setMetaShopOrderTagInclusions(copyInclusions);
    debouncedUpdate(copyInclusions);
  };

  return {
    isSynced,
    loading,
    metaShopOrderTagInclusions,
    updateOrderTagByIndex,
    createOrderTagInclusion,
    removeOrderTagInclusion,
  };
};
